import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getActiveUser } from "services/api/users";
import { updateCurrentUserAPI } from "services/api/users/api";
import { useHandleTextFieldChange } from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";

import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { TextField } from "ui/components/TextField/TextField";
import { EmailTextField } from "ui/components/TextField/EmailTextField";
import { useTranslate } from "helpers";

import { ProfileEditCardProps, ProfileEditFormValues } from "./types";
import { PROFILE_EDIT_FORM_SCHEMA } from "./validation";

const initialProfileEditForm: ProfileEditFormValues = {
  firstName: null,
  lastName: null,
  email: null,
};

const ProfileEditCard: React.FC<ProfileEditCardProps> = (props) => {
  const { show, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const { user } = useSelector(getActiveUser);
  const [formValues, setFormValues] = useState<ProfileEditFormValues>(
    initialProfileEditForm
  );
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (show && user) {
      setFormValues({
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        email: user.email ?? null,
      });
      setErrors({});
    }
  }, [show, user]);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(formValues, PROFILE_EDIT_FORM_SCHEMA, setErrors);
    if (!isValid) return;

    setIsLoading(true);
    try {
      await updateCurrentUserAPI(formValues);
      if (onSuccess) onSuccess();
    } catch {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader
        title="Ažuriranje osobnih podataka"
        icon="/media/images/add-icon.svg"
        onHide={onClose}
      />
      <ModalBody>
        <div className="flex gap-4">
          <div className="w-full m-auto">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="firstName"
                      label="Ime"
                      placeholder="Unesite ime"
                      onChange={handleTextFieldChange}
                      error={errors.firstName}
                      value={formValues.firstName}
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="lastName"
                      label="Prezime"
                      placeholder="Unesite prezime"
                      onChange={handleTextFieldChange}
                      error={errors.lastName}
                      value={formValues.lastName}
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <EmailTextField
                      type="email"
                      name="email"
                      label="Email"
                      placeholder="Unesite email"
                      onChange={handleTextFieldChange}
                      error={errors.email}
                      value={formValues.email}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={onClose}
                  />
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    onClick={handleFormSubmit}
                    disabled={isLoading}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};

export default memo(ProfileEditCard);
